import { useEffect, useState, useCallback } from "react";
import { api, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import {
  Download, Upload, Database, RefreshCw, Loader2, AlertTriangle, CheckCircle2, Rocket,
} from "lucide-react";

/**
 * AdminContentSyncPage — one-shot tool to migrate content collections
 * (lesson_drafts, plans, coupons, learn_content, etc.) from one environment
 * to another via JSON export/import.
 *
 * Use case: data generated in PREVIEW MongoDB needs to be pushed to PRODUCTION
 * after deployment. Export here, then on production admin upload the file.
 */
const COLLECTION_LABELS = {
  lesson_drafts: "AI-drafted lessons (review queue)",
  learn_content: "Approved learn lessons (live)",
  subscription_plans: "Billing plans (Stripe subscriptions)",
  coupons: "Discount coupons (Stripe)",
  promo_codes: "Legacy promo codes",
  pdf_chunks: "RAG PDF chunks (large)",
  reference_chunks: "AI Mentor reference chunks",
  flashcards: "Flashcards",
  learn_topics: "Learn topics list",
  admin_scenarios: "OSCE scenarios",
  announcements: "Banner announcements",
  questions: "MCQ question bank",
  topic_plan: "Topic plan",
};

const AdminContentSyncPage = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // { [name]: "export" | "import" }
  const [lastImport, setLastImport] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  const [dedupeBusy, setDedupeBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/sync/collections");
      setCollections(r.collections || []);
    } catch (e) {
      toast.error("Failed to load sync collections");
    } finally {
      setLoading(false);
    }
    try {
      const s = await api.get("/admin/snapshot/status");
      setSnapshot(s);
    } catch (e) { /* snapshot status is non-critical */ }
  }, []);

  const createSnapshot = async () => {
    setSnapshotBusy(true);
    try {
      const r = await api.post("/admin/snapshot/create");
      toast.success(`Snapshot ${r.manifest?.version} created — deploy to ship it live.`);
      const s = await api.get("/admin/snapshot/status");
      setSnapshot(s);
    } catch (e) {
      toast.error(`Snapshot failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSnapshotBusy(false);
    }
  };

  const runDedupe = async () => {
    setDedupeBusy(true);
    try {
      const r = await api.post("/admin/snapshot/dedupe");
      toast.success(r.removed_total
        ? `Removed ${r.removed_total} duplicate docs.`
        : "No duplicates found — content is clean.");
      const s = await api.get("/admin/snapshot/status");
      setSnapshot(s);
      load();
    } catch (e) {
      toast.error(`Repair failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setDedupeBusy(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const handleExport = async (name) => {
    setBusy((b) => ({ ...b, [name]: "export" }));
    try {
      // Use axios directly so we can get a blob
      const res = await axios.get(`${API}/admin/sync/export/${name}`, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "");
      a.download = `${name}-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${name}`);
    } catch (e) {
      toast.error(`Export failed for ${name}`);
    } finally {
      setBusy((b) => ({ ...b, [name]: undefined }));
    }
  };

  const handleImport = async (name, file, mode) => {
    if (!file) return;
    setBusy((b) => ({ ...b, [name]: "import" }));
    setLastImport(null);
    try {
      if (mode === "upsert") {
        // Dry-run first so the admin sees new / updated / skipped / 0 deleted.
        const fd0 = new FormData();
        fd0.append("file", file);
        const dry = await axios.post(
          `${API}/admin/sync/import/${name}?mode=upsert&dry_run=true`,
          fd0,
          { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } },
        );
        const d = dry.data;
        const ok = window.confirm(
          `Dry-run for ${name}:\n` +
          `• ${d.new} new\n• ${d.updated} updated\n• ${d.unchanged} unchanged\n` +
          `• ${d.skipped_newer} skipped (production newer)\n• ${d.skipped} skipped (no key)\n` +
          `• ${d.deleted} deleted\n\nApply these changes now?`,
        );
        if (!ok) {
          setBusy((b) => ({ ...b, [name]: undefined }));
          return;
        }
      }
      const fd = new FormData();
      fd.append("file", file);
      let url = `${API}/admin/sync/import/${name}?mode=${mode}`;
      if (mode === "replace") {
        const phrase = window.prompt(
          `Destructive replace of '${name}'. This wipes all docs first (a backup is taken).\n\n` +
          `Type exactly:  ${name} REPLACE`,
        );
        if (phrase === null) {
          setBusy((b) => ({ ...b, [name]: undefined }));
          return;
        }
        url += `&confirm=${encodeURIComponent(phrase)}`;
      }
      const res = await axios.post(url, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastImport({ collection: name, ...res.data });
      toast.success(
        mode === "replace"
          ? `${name}: replaced (backup ${res.data.backup})`
          : `${name}: ${res.data.new || 0} new, ${res.data.updated || 0} updated, ${res.data.skipped_newer || 0} kept (newer)`,
      );
      load();
    } catch (e) {
      toast.error(`Import failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy((b) => ({ ...b, [name]: undefined }));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-content-sync-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Database className="h-7 w-7 text-blue-600" />
            Content Sync
          </h1>
          <p className="text-slate-600 mt-1 max-w-3xl">
            Move content collections between environments. <b>Export</b> on the source (e.g. preview)
            then <b>Import</b> the JSON file on the destination (e.g. production) to copy lesson
            drafts, billing plans, coupons, and approved lessons.
          </p>
        </div>
        <button
          onClick={load}
          className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1 px-3 py-2 rounded border border-slate-200 hover:border-slate-300"
          data-testid="admin-sync-refresh"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {/* Deploy Snapshot — the primary workflow */}
      <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-5" data-testid="snapshot-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-2xl">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Rocket className="h-5 w-5 text-blue-600" />
              Deploy Snapshot <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">recommended</span>
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              One click bakes ALL content (questions, lessons, plans, coupons, RAG data) into the
              app code. Then hit <b>Deploy</b> — production auto-syncs on startup. No files to
              download, no manual imports. User accounts and payments are never touched.
            </p>
            {snapshot?.manifest ? (
              <p className="text-xs text-slate-500 mt-2" data-testid="snapshot-info">
                Last snapshot: <b>{snapshot.manifest.version}</b>
                {" "}({Object.values(snapshot.manifest.collections || {}).reduce((a, b) => a + b, 0).toLocaleString()} docs)
                {snapshot.up_to_date
                  ? <span className="text-emerald-600 ml-1">— everything here is published</span>
                  : <span className="text-amber-600 ml-1">— unpublished changes (applied: {snapshot.applied_version || "never"})</span>}
              </p>
            ) : (
              <p className="text-xs text-amber-700 mt-2" data-testid="snapshot-info">No snapshot created yet.</p>
            )}

            {snapshot?.has_unpublished_changes && (
              <div className="mt-3 rounded-lg border border-amber-300 bg-amber-100/70 p-3 text-sm text-amber-900" data-testid="snapshot-drift-warning">
                <div className="flex items-center gap-2 font-semibold">
                  <AlertTriangle className="h-4 w-4" /> You have content changes that production has never seen
                </div>
                <p className="text-xs mt-1">
                  These collections differ from the last published snapshot. Until you click
                  <b> Publish content for deploy</b> and then deploy, the live site keeps serving the older content.
                </p>
                <ul className="text-xs mt-2 space-y-0.5">
                  {(snapshot.changed_collections || []).map((n) => {
                    const d = snapshot.drift?.[n] || {};
                    return (
                      <li key={n} data-testid={`snapshot-drift-${n}`}>
                        <b>{n}</b>: live {(d.live_count ?? 0).toLocaleString()} vs published{" "}
                        {d.snapshot_count === null || d.snapshot_count === undefined
                          ? "—" : d.snapshot_count.toLocaleString()}
                        {d.reason ? ` (${d.reason})` : ""}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {snapshot?.duplicate_total > 0 && (
              <div className="mt-3 rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900" data-testid="snapshot-duplicate-warning">
                <div className="flex items-center gap-2 font-semibold">
                  <AlertTriangle className="h-4 w-4" /> {snapshot.duplicate_total.toLocaleString()} duplicate content docs in this database
                </div>
                <ul className="text-xs mt-2 space-y-0.5">
                  {Object.entries(snapshot.duplicates || {}).map(([n, v]) => (
                    <li key={n}><b>{n}</b>: {v.toLocaleString()} extra copies</li>
                  ))}
                </ul>
                <button
                  onClick={runDedupe}
                  disabled={dedupeBusy}
                  className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded bg-rose-600 text-white text-xs font-medium hover:bg-rose-700 disabled:opacity-50"
                  data-testid="snapshot-dedupe-btn"
                >
                  {dedupeBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Database className="h-3.5 w-3.5" />}
                  {dedupeBusy ? "Repairing…" : "Remove duplicates now"}
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-col items-stretch gap-2">
            <button
              onClick={createSnapshot}
              disabled={snapshotBusy}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
              data-testid="snapshot-create-btn"
            >
              {snapshotBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
              {snapshotBusy ? "Publishing…" : "Publish content for deploy"}
            </button>
            <button
              onClick={runDedupe}
              disabled={dedupeBusy}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-50"
              data-testid="snapshot-dedupe-check-btn"
            >
              {dedupeBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
              {dedupeBusy ? "Checking…" : "Check for duplicates"}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2">
        <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
        <div>
          <b>Production is the source of truth for content — deploys ship code only.</b>
          {" "}To push preview → live: click <b>Export</b> here, then on production open this
          page and click <b>Upsert</b> (it dry-runs first, never deletes, and keeps any newer
          live docs). To refresh preview from live: <b>Export</b> on production, then <b>Upsert</b>
          here. <b>Replace</b> is destructive — it takes a backup and needs you to type
          "<i>collection</i> REPLACE".
        </div>
      </div>

      {lastImport && (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900 flex gap-2">
          <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div>
            Last import — <b>{lastImport.collection}</b> ({lastImport.mode}):
            {" "}{lastImport.inserted || 0} inserted, {lastImport.updated || 0} updated,
            {" "}{lastImport.unchanged || 0} unchanged, {lastImport.skipped || 0} skipped
            (out of {lastImport.total_in_file} in file).
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden bg-white">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="p-3">Collection</th>
                <th className="p-3 text-right">Docs in this DB</th>
                <th className="p-3">Export</th>
                <th className="p-3">Import (upsert)</th>
                <th className="p-3">Import (replace)</th>
              </tr>
            </thead>
            <tbody>
              {collections.map((c) => {
                const label = COLLECTION_LABELS[c.name] || c.name;
                const isBusy = busy[c.name];
                return (
                  <tr key={c.name} className="border-t border-slate-100">
                    <td className="p-3">
                      <div className="font-medium text-slate-900">{c.name}</div>
                      <div className="text-xs text-slate-500">{label}</div>
                    </td>
                    <td className="p-3 text-right font-mono">{c.count.toLocaleString()}</td>
                    <td className="p-3">
                      <button
                        onClick={() => handleExport(c.name)}
                        disabled={isBusy}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                        data-testid={`sync-export-${c.name}`}
                      >
                        {isBusy === "export" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        Export
                      </button>
                    </td>
                    <td className="p-3">
                      <ImportButton
                        collectionName={c.name}
                        mode="upsert"
                        onImport={(f) => handleImport(c.name, f, "upsert")}
                        busy={isBusy === "import"}
                      />
                    </td>
                    <td className="p-3">
                      <ImportButton
                        collectionName={c.name}
                        mode="replace"
                        onImport={(f) => handleImport(c.name, f, "replace")}
                        busy={isBusy === "import"}
                        danger
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ImportButton = ({ collectionName, mode, onImport, busy, danger }) => {
  const inputId = `sync-import-${mode}-${collectionName}`;
  return (
    <label
      htmlFor={inputId}
      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded border cursor-pointer ${
        danger
          ? "border-rose-200 text-rose-700 hover:bg-rose-50"
          : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
      } ${busy ? "opacity-50 pointer-events-none" : ""}`}
      data-testid={`sync-import-label-${mode}-${collectionName}`}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
      {mode === "replace" ? "Replace" : "Upsert"}
      <input
        id={inputId}
        type="file"
        accept="application/json,.json"
        className="hidden"
        data-testid={`sync-import-input-${mode}-${collectionName}`}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = ""; // reset so same file can be re-picked
          if (f) onImport(f);
        }}
      />
    </label>
  );
};

export default AdminContentSyncPage;
